/**
 * Tag Icon Renderer - Tag node icon
 *
 * Renders a tag icon used for representing tag nodes
 * in the graph visualization.
 */

import { getIconConfig } from '@/components/icons/nodeIcons/config/iconConfig';
import { getThemeColors } from '@/components/icons/nodeIcons/config/themeColors';
import {
  calculateIconPosition,
  drawCircleBackground,
  setupCanvas,
} from '@/components/icons/nodeIcons/renderers/base';

/**
 * Draw tag icon on canvas
 *
 * Internal helper function that draws a tag shape with hole.
 *
 * @param ctx - Canvas rendering context
 * @param x - X coordinate for tag
 * @param y - Y coordinate for tag
 * @param size - Size of the tag
 * @param color - Color for the tag outline
 */
const drawTag = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  color: string = '#000000'
): void => {
  const scale = size / 24;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.fillStyle = 'none';

  // Main tag shape path
  ctx.beginPath();
  ctx.moveTo(9.568, 3);
  ctx.lineTo(5.25, 3);
  // Rounded rectangle for the tag base
  ctx.arcTo(3, 3, 3, 5.25, 2.25);
  ctx.lineTo(3, 9.568);
  // Calculate the diagonal line with proper angles
  ctx.lineTo(13.24, 19.149);
  // Tag tip with rounded corners
  ctx.arcTo(20.74, 21.089, 20.74, 13.589, 3.5);
  ctx.lineTo(11.16, 3.66);
  ctx.arcTo(9.568, 3, 9.568, 3, 2.25);
  ctx.stroke();

  // Small dot/hole in the tag
  ctx.beginPath();
  ctx.rect(6, 6, 0.008, 0.008);
  ctx.stroke();

  ctx.restore();
};

/**
 * Create tag icon data URL
 *
 * Generates a tag icon with circular background for use as
 * a tag node image in the graph visualization.
 *
 * @param currentTheme - Current theme ('light' or 'dark')
 * @returns Data URL string of the rendered icon
 */
export const createTagIconDataURL = (
  currentTheme: 'light' | 'dark'
): string => {
  const config = getIconConfig('tag');
  const colors = getThemeColors('tag', currentTheme);
  const { canvas, ctx, size, centerX, centerY } = setupCanvas(config);

  // Draw circle background and border
  drawCircleBackground(ctx, centerX, centerY, size, config.scale, colors);

  // Calculate icon position
  const {
    x: tagX,
    y: tagY,
    scale: tagScale,
  } = calculateIconPosition(centerX, centerY, size, config);

  // Draw tag icon
  drawTag(ctx, tagX, tagY, config.iconWidth * tagScale, colors.icon);

  // Convert to data URL with higher quality
  return canvas.toDataURL('image/png', 1.0);
};
